import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  KeyRound,
  Building2,
  Code2,
  History,
  Settings,
  Database,
  UserRoundSearch,
  UserCog,
  ChevronLeft,
  ChevronDown,
  LogOut,
  Check,
  Search,
  ShieldCheck,
  Webhook,
  ScrollText,
  RefreshCcw,
} from "lucide-react";
import { APP_SHORT_ORG } from "@/branding";
import { DatalyLogo } from "@/components/DatalyLogo";
import { cn } from "@/lib/cn";

export type NavKey =
  | "overview"
  | "secrets"
  | "organization"
  | "api-keys"
  | "audit"
  | "admin-profile"
  | "settings"
  | "database"
  | "directory"
  | "security"
  | "webhooks"
  | "approvals"
  | "rotation";

type Org = { id: string; name: string };

export type SidebarProps = {
  active: NavKey;
  onChange: (k: NavKey) => void;
  orgs: Org[];
  orgId: string | null;
  onOrgChange: (id: string) => void;
  user: { email: string; displayName?: string | null };
  roleLabel?: string | null;
  onSignOut: () => void;
  /** Vault + Secrets nav entries — hide for `public_user`, platform viewers. */
  showVaultSecrets?: boolean;
  /** Organization tab (tenant team or platform org list). Hidden for `public_user`. */
  showOrganizationNav?: boolean;
  /** Workspace dropdown in the sidebar. Hidden for `public_user` (label only). */
  workspacePicker?: boolean;
  /** Audit — workspace admins only (`client_admin` / `dataly_admin`). */
  showAudit?: boolean;
  /** Admin profile — outbound mail From + display name (workspace admins). */
  showAdminProfile?: boolean;
  showApiKeys?: boolean;
  showDatabase?: boolean;
  showDirectory?: boolean;
  showSecurity?: boolean;
  showWebhooks?: boolean;
  showApprovals?: boolean;
  showRotation?: boolean;
};

type NavSpec = {
  key: NavKey;
  label: string;
  icon: typeof LayoutDashboard;
  show?: boolean;
};

export function Sidebar({
  active,
  onChange,
  orgs,
  orgId,
  onOrgChange,
  user,
  roleLabel,
  onSignOut,
  showVaultSecrets = true,
  showOrganizationNav = true,
  workspacePicker = true,
  showAudit = true,
  showAdminProfile = false,
  showApiKeys = true,
  showDatabase = false,
  showDirectory = false,
  showSecurity = true,
  showWebhooks = false,
  showApprovals = true,
  showRotation = false,
}: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  const items: NavSpec[] = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "secrets", label: "Secrets", icon: KeyRound, show: showVaultSecrets },
    {
      key: "organization",
      label: "Organization",
      icon: Building2,
      show: showOrganizationNav,
    },
    { key: "api-keys", label: "API keys", icon: Code2, show: showApiKeys },
    { key: "approvals", label: "Approvals", icon: ScrollText, show: showApprovals },
    { key: "rotation", label: "Rotation", icon: RefreshCcw, show: showRotation },
    { key: "webhooks", label: "Webhooks", icon: Webhook, show: showWebhooks },
    { key: "audit", label: "Audit", icon: History, show: showAudit },
    {
      key: "admin-profile",
      label: "Admin profile",
      icon: UserCog,
      show: showAdminProfile,
    },
    { key: "security", label: "Security", icon: ShieldCheck, show: showSecurity },
    { key: "settings", label: "Settings", icon: Settings },
    { key: "directory", label: "All users", icon: UserRoundSearch, show: showDirectory },
    { key: "database", label: "Database", icon: Database, show: showDatabase },
  ];

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 64 : 256 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="relative flex shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ boxShadow: "var(--shadow-sidebar)" }}
    >
      {/* Collapse toggle */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] shadow transition hover:bg-[var(--color-surface-3)]"
      >
        <ChevronLeft
          className={cn(
            "h-3.5 w-3.5 text-[var(--color-text-muted)] transition-transform",
            collapsed && "rotate-180",
          )}
        />
      </button>

      {/* Brand */}
      <div className="flex shrink-0 items-center gap-3 overflow-hidden border-b border-[var(--color-border)] p-4">
        <DatalyLogo size="sm" className="rounded-md" />
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              key="brand"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15 }}
              className="min-w-0 overflow-hidden whitespace-nowrap"
            >
              <p className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-subtle)]">
                {APP_SHORT_ORG}
              </p>
              <p className="truncate text-sm font-semibold text-[var(--color-text)]">
                Secret Manager
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Org switcher */}
      <div className={cn("shrink-0 px-3 pt-3", collapsed && "px-2")}>
        <OrgSwitcher
          orgs={orgs}
          orgId={orgId}
          onChange={onOrgChange}
          collapsed={collapsed}
          interactive={workspacePicker}
        />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
        {!collapsed && (
          <p className="px-5 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
            Workspace
          </p>
        )}
        <ul className="space-y-0.5">
          {items
            .filter((i) => i.show !== false)
            .map((i) => {
              const Icon = i.icon;
              const isActive = active === i.key;
              return (
                <li key={i.key}>
                  <button
                    type="button"
                    onClick={() => onChange(i.key)}
                    className={cn(
                      "mx-2 flex w-[calc(100%-1rem)] items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors",
                      isActive
                        ? "bg-[var(--color-accent)] text-white font-semibold"
                        : "text-[var(--color-text)] hover:bg-[var(--color-surface-3)]",
                      collapsed && "justify-center px-0",
                    )}
                    title={collapsed ? i.label : undefined}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span>{i.label}</span>}
                  </button>
                </li>
              );
            })}
        </ul>
      </nav>

      {/* Footer: user card + logout */}
      <div className="shrink-0 border-t border-[var(--color-border)] p-3">
        {!collapsed && (
          <div className="mb-2 flex items-center gap-2.5 rounded-lg bg-[var(--color-surface-2)] px-3 py-2">
            <div
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
              }}
            >
              {(user.displayName || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-xs font-medium text-[var(--color-text)]"
                title={user.email}
              >
                {user.displayName || user.email}
              </p>
              {roleLabel && (
                <p className="truncate text-[10px] font-medium text-[var(--color-text-subtle)]">
                  {roleLabel}
                </p>
              )}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onSignOut}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-3)]",
            collapsed && "justify-center px-0",
          )}
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </motion.aside>
  );
}

function OrgSwitcher({
  orgs,
  orgId,
  onChange,
  collapsed,
  interactive = true,
}: {
  orgs: Org[];
  orgId: string | null;
  onChange: (id: string) => void;
  collapsed: boolean;
  /** When false, show current workspace name only (e.g. `public_user`). */
  interactive?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [orgQuery, setOrgQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const current = orgs.find((o) => o.id === orgId) ?? orgs[0] ?? null;

  const filteredOrgs = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => o.name.toLowerCase().includes(q));
  }, [orgs, orgQuery]);

  useEffect(() => {
    if (!open) setOrgQuery("");
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (!current) {
    return collapsed ? null : (
      <div className="rounded-lg border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-subtle)]">
        No organization
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="flex justify-center" title={current.name}>
        <div
          className="grid h-9 w-9 place-items-center rounded-md bg-[var(--color-surface-2)] text-[13px] font-semibold text-[var(--color-text)]"
          style={{ border: "1px solid var(--color-border)" }}
        >
          {current.name.slice(0, 1).toUpperCase()}
        </div>
      </div>
    );
  }

  const workspaceInner = (
    <>
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-[var(--color-surface)] text-[12px] font-semibold text-[var(--color-text)] ring-1 ring-[var(--color-border)]">
        {current.name.slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-text-subtle)]">
          Workspace
        </p>
        <p className="truncate text-sm font-medium text-[var(--color-text)]">{current.name}</p>
      </div>
      {interactive ? (
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform",
            open && "rotate-180",
          )}
        />
      ) : null}
    </>
  );

  if (!interactive) {
    return (
      <div className="flex w-full items-center gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-left">
        {workspaceInner}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-left transition hover:border-[var(--color-border-strong)]"
      >
        {workspaceInner}
      </button>

      {open && orgs.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 flex max-h-72 flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
          <div className="sticky top-0 border-b border-[var(--color-border)] bg-[var(--color-surface)] p-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-subtle)]" />
              <input
                type="search"
                autoComplete="off"
                placeholder="Search organizations…"
                aria-label="Search organizations by name"
                value={orgQuery}
                onChange={(e) => setOrgQuery(e.target.value)}
                className="field-input w-full py-1.5 pl-8 pr-2 text-sm"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto p-1">
            {filteredOrgs.length === 0 ? (
              <p className="px-2 py-4 text-center text-xs text-[var(--color-text-subtle)]">
                No organizations match “{orgQuery.trim()}”.
              </p>
            ) : (
              filteredOrgs.map((o) => {
                const active = o.id === orgId;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-[var(--color-text)] transition hover:bg-[var(--color-surface-3)]"
                  >
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded bg-[var(--color-surface-2)] text-[10px] font-semibold ring-1 ring-[var(--color-border)]">
                      {o.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="flex-1 truncate">{o.name}</span>
                    {active && <Check className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
